import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExplainNode, explainChildren, formatExplainValue, isExplainNode } from './explain';
import { useRuleMetadata } from './metadata/RuleMetadataProvider';
import { descriptorForComparison, descriptorOfFact } from './editor/builder/valueDescriptor';
import { RefOption, useEntityRefOptions } from './editor/builder/entityRefOptions';
import { argsText, describeArgs, factParametersOf } from './factArguments';

interface ComponentProps {
  explain: unknown;
  /** The company whose rule this is — the admin's chosen one, or absent for the user's own. */
  companyId?: string;
}

/**
 * The condition tree annotated with what actually happened: every node marked pass or fail, and
 * the real value shown beside the expected one. This is the difference between a debuggable
 * engine and an opaque one, and it is the answer to "why did my rule not fire".
 *
 * `explain` has no schema on the wire, so anything unrecognised falls back to raw JSON rather
 * than throwing.
 */
const ExplainTree: React.FC<ComponentProps> = ({ explain, companyId }) => {
  const { tr } = useTranslationLgs();

  if (!isExplainNode(explain)) {
    if (explain === null || explain === undefined) return <div className="text-sm text-content-secondary">{tr('AiDispatcher.noExplain', 'No trace was returned.')}</div>;
    return <pre className="overflow-x-auto rounded bg-base-tertiary p-2 text-xs">{JSON.stringify(explain, null, 2)}</pre>;
  }

  return (
    <div className="flex flex-col gap-2">
      <Node node={explain} companyId={companyId} />
      {/* AND stops at the first false and OR at the first true, so a trace can be shorter than the
          rule. Saying so once beats marking absent clauses as passed, which they were not. */}
      <span className="text-xs text-content-tertiary">
        {tr('AiDispatcher.shortCircuitNote', 'Evaluation stops at the first condition that decides the outcome, so later conditions may not appear here.')}
      </span>
    </div>
  );
};

/** An id → name lookup over one picker list, or undefined when there is no list to look in. */
const nameLookup = (options: RefOption[], supported: boolean): ((id: string) => string | undefined) | undefined =>
  supported ? id => options.find(option => option.value === id)?.label : undefined;

const Node: React.FC<{ node: ExplainNode; companyId?: string }> = ({ node, companyId }) => {
  const { tr } = useTranslationLgs();
  const { factByKey, operatorByKey } = useRuleMetadata();
  const children = explainChildren(node);
  const passed = node.result;

  // Entity references (labels, carriers, users…) are stored by id. The actual value is the fact's
  // own kind; the expected one may be the operator's (`in group` compares a country to a group).
  const fact = node.type === 'COMPARISON' && node.fact ? factByKey(node.fact) : undefined;
  const actualRefs = useEntityRefOptions(fact ? descriptorOfFact(fact).entityRefType : undefined, companyId);
  const expectedRefs = useEntityRefOptions(fact ? descriptorForComparison(fact, operatorByKey(node.operator ?? '')).entityRefType : undefined, companyId);
  const actualName = nameLookup(actualRefs.options, actualRefs.supported);
  const expectedName = nameLookup(expectedRefs.options, expectedRefs.supported);

  const marker = (
    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{passed ? '✓' : '✕'}</span>
  );

  if (node.type === 'COMPARISON') {
    return (
      <div className={`rounded-md border border-solid p-2 ${passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
        <div className="flex flex-wrap items-center gap-2">
          {marker}
          <span className="font-mono text-sm">
            {node.fact}
            {argsText(node.args)}
          </span>
          <span className="text-sm text-content-secondary">{node.operator}</span>
          <span className="font-mono text-sm">{formatExplainValue(node.expected, expectedName)}</span>
        </div>
        {/* Without its arguments the line is ambiguous: the same fact measures different spans. */}
        {node.args && Object.keys(node.args).length > 0 && (
          <div className="mt-1 pl-7 text-xs text-content-tertiary">{describeArgs(tr, factParametersOf(fact), node.args)}</div>
        )}
        <div className="mt-1 pl-7 text-sm text-content-secondary">
          {tr('AiDispatcher.actualValue', 'actual')}: <span className="font-mono text-content-primary">{formatExplainValue(node.actual, actualName)}</span>
        </div>
        {/* Set when the comparison could not be made at all — an unknown fact or a bad value shape. */}
        {node.note && <div className="mt-1 pl-7 text-sm text-amber-700">{node.note}</div>}
      </div>
    );
  }

  if (node.type === 'ALWAYS') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-solid border-green-200 bg-green-50 p-2">
        {marker}
        <span className="text-sm">{tr('AiDispatcher.alwaysMatches', 'The rule has no condition, so it always matches.')}</span>
      </div>
    );
  }

  const groupLabel =
    node.type === 'AND'
      ? tr('AiDispatcher.allOf', 'All of')
      : node.type === 'OR'
        ? tr('AiDispatcher.anyOf', 'Any of')
        : node.type === 'NOT'
          ? tr('AiDispatcher.not', 'not')
          : node.type;

  return (
    <div className={`rounded-md border border-solid p-2 ${passed ? 'border-green-200' : 'border-red-200'}`}>
      <div className="flex items-center gap-2">
        {marker}
        <span className="text-sm font-medium uppercase text-content-secondary">{groupLabel}</span>
      </div>
      {node.note && <div className="mt-1 pl-7 text-sm text-amber-700">{node.note}</div>}
      <div className="mt-2 flex flex-col gap-2 pl-4">
        {children.map((child, index) => (
          <Node key={index} node={child} companyId={companyId} />
        ))}
      </div>
    </div>
  );
};

export default ExplainTree;
